/**
 * Browser persistence for the in-process backend: one Web Storage entry per document,
 * key `<prefix><collection>/<id>`. Works with `window.localStorage` or any object implementing the
 * Web Storage API subset below (tests use an in-memory implementation).
 */
import { BackendError } from '../../shared/protocol/errors';
import { type StorageAdapter, type StorageBatchOp, assertCollection, assertDocId, prepareBatch } from './storage';

export interface WebStorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class LocalStorageAdapter implements StorageAdapter {
  readonly kind = 'localStorage';
  readonly volatile = false;

  constructor(
    private readonly storage: WebStorageLike,
    private readonly prefix = 'fwp.backend.v1/',
  ) {}

  async open(): Promise<void> {}

  private key(collection: string, id: string): string {
    return `${this.prefix}${collection}/${id}`;
  }

  async get<T>(collection: string, id: string): Promise<T | null> {
    const raw = this.storage.getItem(this.key(collection, id));
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  async put<T>(collection: string, id: string, value: T): Promise<void> {
    assertCollection(collection);
    assertDocId(id);
    const raw = JSON.stringify(value);
    if (raw === undefined) throw new Error(`put: the value of ${collection}/${id} is not a JSON value`);
    try {
      this.storage.setItem(this.key(collection, id), raw);
    } catch (err) {
      throw new BackendError('UNAVAILABLE', 'Local storage is full or unavailable', { reason: 'storage_full', cause: String(err) });
    }
  }

  async delete(collection: string, id: string): Promise<boolean> {
    const k = this.key(collection, id);
    const had = this.storage.getItem(k) !== null;
    this.storage.removeItem(k);
    return had;
  }

  async list<T>(collection: string): Promise<T[]> {
    const p = `${this.prefix}${collection}/`;
    const keys: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const k = this.storage.key(i);
      if (k !== null && k.startsWith(p)) keys.push(k);
    }
    const out: T[] = [];
    for (const k of keys) {
      const raw = this.storage.getItem(k);
      if (raw === null) continue;
      try {
        out.push(JSON.parse(raw) as T);
      } catch {
        /* skip corrupted entries */
      }
    }
    return out;
  }

  /**
   * Best effort: Web Storage has no transactions. Every op is validated and serialized first, then all of them are
   * applied in ONE synchronous pass (no other code of the page runs in between, and a closed tab cannot interrupt a
   * synchronous pass half way). When the browser refuses a write (quota), the documents already written by this batch
   * are restored to their previous values and the batch fails with UNAVAILABLE.
   */
  async batch(ops: readonly StorageBatchOp[]): Promise<void> {
    const writes = prepareBatch(ops).map((w) => ({ key: this.key(w.collection, w.id), raw: w.raw }));
    const before = writes.map((w) => this.storage.getItem(w.key));
    let i = 0;
    try {
      for (; i < writes.length; i++) {
        const w = writes[i];
        if (w.raw === null) this.storage.removeItem(w.key);
        else this.storage.setItem(w.key, w.raw);
      }
    } catch (err) {
      for (let j = i - 1; j >= 0; j--) {
        try {
          const prev = before[j];
          if (prev === null) this.storage.removeItem(writes[j].key);
          else this.storage.setItem(writes[j].key, prev);
        } catch {
          /* nothing more can be done without storage */
        }
      }
      throw new BackendError('UNAVAILABLE', 'Local storage is full or unavailable', { reason: 'storage_full', cause: String(err) });
    }
  }

  async flush(): Promise<void> {}
  async close(): Promise<void> {}
}

/** In-memory Web Storage implementation (Node in-process mode and tests). */
export class MemoryWebStorage implements WebStorageLike {
  private readonly map = new Map<string, string>();
  get length(): number {
    return this.map.size;
  }
  key(index: number): string | null {
    return [...this.map.keys()][index] ?? null;
  }
  getItem(key: string): string | null {
    return this.map.has(key) ? this.map.get(key)! : null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, String(value));
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
  clear(): void {
    this.map.clear();
  }
}
